import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { FlexModule } from '@angular/flex-layout/flex';
import { BusinessSearchComponent, NameComponent, ApplicationDialogService, PortalHotToastService } from '@portal-workspace/grow-ui-library';
import { ApplicationService } from '../../service/application.service';
import { BasiqService } from '../../service/basiq.service';
import { BusinessSearchValue, NameComponentValue } from '@portal-workspace/grow-shared-library';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BasiqManageConsentRequest, TwoFAVerificationDialogResult } from '@portal-workspace/grow-shared-library';
import { finalize } from 'rxjs/operators';
import { MatRadioModule } from '@angular/material/radio';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { setupUntilDestroy } from '@portal-workspace/grow-ui-library';
import { Router } from '@angular/router';
@UntilDestroy({arrayName: 'subscriptions'})
@Component({
  templateUrl: './basiq-add-requirement-page.html',
  styleUrls: ['./basiq-add-requirement-page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    ReactiveFormsModule,
    FlexModule,
    BusinessSearchComponent,
    NameComponent,
    MatFormFieldModule,
    MatInputModule,
    AsyncPipe,
    MatRadioModule
  ]
})
 
export class BasiqAddRequirementPage implements OnInit, OnDestroy {
  businessDetails = new FormControl<BusinessSearchValue | null>(null, [Validators.required]);
  
  // Personal details form controls - using custom component types
  givenName = new FormControl<NameComponentValue | null>(null, [Validators.required]);
  surName = new FormControl<NameComponentValue | null>(null, [Validators.required]);
  otpDeliveryMethod = new FormControl<'sms' | 'email'>('sms', { nonNullable: true });

  subscriptions: Subscription[] = [];
  constructor(
    private applicationService: ApplicationService,
    private basiqService: BasiqService,
    private toastService: PortalHotToastService,
    private applicationDialogService: ApplicationDialogService,
    private router: Router,
    private elRef: ElementRef
  ) {}

  businessSearchPlaceholder = 'Search for business';
  isVerifying = false;

  ngOnInit() {
    setupUntilDestroy(this);
    // Ensure the page starts at the top of the page
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    // Reset all form controls when component initializes
    this.resetForm();
    setTimeout(() => {
      this.resetForm();
    }, 200);

    this.businessDetails.valueChanges.subscribe(value => {
      if(value?.abn) {
        this.isFormValid();
      }
    });


  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    }); 
  }

  resetForm() {
 
    
    this.businessDetails.reset();
    this.givenName.reset();
    this.surName.reset();
     
 

    
  }

  searchBusinessFn = (query: string) => {
    return this.applicationService.businessSearchFn(query);
  };

  isFormValid(): boolean {
    return this.businessDetails.valid && 
           this.givenName.valid && 
           this.surName.valid
  }

  send2FaCodeToBusinessUser() {
    this.businessDetails.markAsTouched();
    this.givenName.markAsTouched();
    this.surName.markAsTouched();
  
    if (!this.isFormValid()) {
      this.toastService.error('Please fill in all required fields correctly');
      return;
    }
  
    const businessValue = this.businessDetails.value;
  
    if (!businessValue?.abn || !businessValue?.organisationName) {
      this.toastService.error('Business ABN and name are required');
      return;
    }
  
    const request: BasiqManageConsentRequest = {
      businessDetails: {
        name: businessValue.organisationName,
        abn: businessValue.abn,
        acn: businessValue.acn
      },
      personalDetails: {
        givenName: this.givenName.value!,
        surName: this.surName.value!
      },
      otpDeliveryMethod: this.otpDeliveryMethod.value 
    };
  
    this.isVerifying = true;
  
    this.basiqService
      .send2FaCodeToBusinessUserFn(request)
      .pipe(
        this.toastService.spinnerObservable(),
        finalize(() => {
          this.isVerifying = false;
        })
      )
      .subscribe(
        response => {
          if (response.status && response.payload) {
            // Open verification dialog - it now handles all errors internally
            this.applicationDialogService.openTwoFAVerificationDialog({
              ...request, 
              authenticationValue: this.otpDeliveryMethod.value === 'sms' ? response.payload.mobileNumber : response.payload.email
            }).afterClosed().subscribe((result: TwoFAVerificationDialogResult | undefined) => {
              if (result?.verified) {
                // User successfully verified - proceed to manage consent
                this.getBasiqManageConsentLink();
              } else {
                // User cancelled or closed dialog without verifying
                console.log('User cancelled 2FA verification or verification failed');
              }
            });
          } else {
            this.toastService.error(response.message || 'Failed to send verification code');
          }
        },
        (error: any) => {
          console.error('Error sending verification code:', error);
          this.toastService.error(error.error?.message || 'An error occurred while sending the verification code');
        }
      );
  }

  getBasiqManageConsentLink() {
    const businessValue = this.businessDetails.value;
    
    if (!businessValue?.abn) {
      this.toastService.error('Business ABN is required');
      return;
    }
    
    this.basiqService.getBasiqManageConsentLink(businessValue.abn)
      .pipe(this.toastService.spinnerObservable())
      .subscribe(
        response => {
          if (response.status && response.payload) {
            // Redirect directly to the Basiq URL
            window.location.href = response.payload;
          } else {
            this.toastService.error(response.message || 'Failed to generate manage consent link');
          }
        },
        error => {
          this.toastService.error(error.error?.message || 'An error occurred while generating manage consent link');
          console.error('Basiq manage consent link error:', error);
        }
      );
  }
}